/**
 * @module features/devices/utils/csvValidator
 * @description Client-side CSV parsing, pre-validation and error report download.
 * Runs instantly in the browser without API calls, providing immediate stats on
 * valid vs invalid rows and exportable diagnostics.
 */
import { OS_HASH_REGEX } from "../types";
import { isValidMac, normalizeMac } from "./macFormatter";

export interface ClientValidationError {
  row: number;
  column: string;
  identifier: string;
  reason: string;
}

export interface ClientValidationSummary {
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: ClientValidationError[];
  fatalError?: string;
}

/**
 * Parses a single CSV line taking quoted fields into account.
 */
function parseCsvLine(text: string): string[] {
  const result: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    if (char === '"') {
      if (inQuotes && text[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      result.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  result.push(current.trim());
  return result;
}

/**
 * Validates a CSV file entirely client-side.
 */
export function validateCsvClientSide(
  csvContent: string,
  expectedDeviceType: string
): ClientValidationSummary {
  const rawLines = csvContent.split(/\r?\n/);
  // Filter trailing empty lines
  const lines = rawLines.filter((line, idx) => line.trim().length > 0 || idx < rawLines.length - 1);

  if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === "")) {
    return {
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      errors: [],
      fatalError: "The uploaded CSV file is empty.",
    };
  }

  // Header row is line 1
  const headerCols = parseCsvLine(lines[0]).map((h) => h.toLowerCase().replace(/^["']|["']$/g, "").trim());
  const headerIndex = new Map<string, number>();
  headerCols.forEach((col, idx) => headerIndex.set(col, idx));

  // Required columns
  const isMacRequired = expectedDeviceType === "display_module";
  const requiredCols = ["device_name", "device_type"];
  if (isMacRequired) {
    requiredCols.push("mac_address");
  }
  if (expectedDeviceType === "display_module") {
    requiredCols.push("os_name", "os_version", "os_hash");
  }

  const missingCols = requiredCols.filter((col) => !headerIndex.has(col));
  if (missingCols.length > 0) {
    return {
      totalRows: Math.max(0, lines.length - 1),
      validRows: 0,
      invalidRows: Math.max(0, lines.length - 1),
      errors: [
        {
          row: 1,
          column: "Header",
          identifier: "N/A",
          reason: `Missing required column(s): ${missingCols.join(", ")}`,
        },
      ],
      fatalError: `CSV is missing required column(s): ${missingCols.join(", ")}`,
    };
  }

  const dataLines = lines.slice(1);
  const totalRows = dataLines.length;

  if (totalRows === 0) {
    return {
      totalRows: 0,
      validRows: 0,
      invalidRows: 0,
      errors: [],
      fatalError: "CSV has a header row but contains no data rows.",
    };
  }

  if (totalRows > 500) {
    return {
      totalRows,
      validRows: 0,
      invalidRows: totalRows,
      errors: [
        {
          row: 1,
          column: "File",
          identifier: "N/A",
          reason: `Batch size exceeds limit: ${totalRows} rows found, maximum is 500 rows.`,
        },
      ],
      fatalError: `Batch size exceeds maximum limit of 500 rows (${totalRows} rows found).`,
    };
  }

  const errors: ClientValidationError[] = [];
  const seenMacsInFile = new Map<string, number>();
  let validRows = 0;

  dataLines.forEach((lineText, index) => {
    const lineNum = index + 2; // Line 1 is header, so data starts at Line 2
    if (!lineText.trim()) return; // skip blank line

    const cols = parseCsvLine(lineText);
    const getVal = (colName: string) => {
      const idx = headerIndex.get(colName);
      if (idx === undefined || idx >= cols.length) return "";
      return cols[idx].replace(/^["']|["']$/g, "").trim();
    };

    const deviceName = getVal("device_name");
    const deviceType = getVal("device_type");
    const macAddress = getVal("mac_address");
    const registeredId = getVal("registered_device_id");

    const identifier = macAddress || registeredId || deviceName || `Line ${lineNum}`;
    let rowHasError = false;

    // 1. Device Name checks
    if (!deviceName) {
      errors.push({
        row: lineNum,
        column: "device_name",
        identifier,
        reason: "Device name is required.",
      });
      rowHasError = true;
    } else if (deviceName.length > 100) {
      errors.push({
        row: lineNum,
        column: "device_name",
        identifier,
        reason: "Device name cannot exceed 100 characters.",
      });
      rowHasError = true;
    }

    // 2. Device Type checks
    if (!deviceType) {
      errors.push({
        row: lineNum,
        column: "device_type",
        identifier,
        reason: "Device type is required.",
      });
      rowHasError = true;
    } else if (deviceType.toLowerCase() !== expectedDeviceType.toLowerCase()) {
      errors.push({
        row: lineNum,
        column: "device_type",
        identifier,
        reason: `Device type '${deviceType}' does not match expected batch type '${expectedDeviceType}'.`,
      });
      rowHasError = true;
    }

    // 3. MAC Address checks & File Duplicate check
    if (isMacRequired && !macAddress) {
      errors.push({
        row: lineNum,
        column: "mac_address",
        identifier,
        reason: "MAC address is required for display module devices.",
      });
      rowHasError = true;
    } else if (macAddress) {
      if (!isValidMac(macAddress)) {
        errors.push({
          row: lineNum,
          column: "mac_address",
          identifier,
          reason: `Invalid MAC address '${macAddress}'. Expected format XX:XX:XX:XX:XX:XX.`,
        });
        rowHasError = true;
      } else {
        const normMac = normalizeMac(macAddress);
        if (seenMacsInFile.has(normMac)) {
          errors.push({
            row: lineNum,
            column: "mac_address",
            identifier,
            reason: `Duplicate MAC address '${macAddress}' already defined on line ${seenMacsInFile.get(normMac)}.`,
          });
          rowHasError = true;
        } else {
          seenMacsInFile.set(normMac, lineNum);
        }
      }
    }

    // 4. Optional Registered ID check
    if (registeredId && registeredId.length > 100) {
      errors.push({
        row: lineNum,
        column: "registered_device_id",
        identifier,
        reason: "Registered device ID cannot exceed 100 characters.",
      });
      rowHasError = true;
    }

    // 5. Subtype checks (display_module)
    if (expectedDeviceType === "display_module") {
      const osName = getVal("os_name");
      const osVersion = getVal("os_version");
      const osHash = getVal("os_hash");

      if (!osName) {
        errors.push({
          row: lineNum,
          column: "os_name",
          identifier,
          reason: "OS name is required for display_module.",
        });
        rowHasError = true;
      } else if (osName.length > 100) {
        errors.push({
          row: lineNum,
          column: "os_name",
          identifier,
          reason: "OS name cannot exceed 100 characters.",
        });
        rowHasError = true;
      }

      if (!osVersion) {
        errors.push({
          row: lineNum,
          column: "os_version",
          identifier,
          reason: "OS version is required for display_module.",
        });
        rowHasError = true;
      } else if (osVersion.length > 50) {
        errors.push({
          row: lineNum,
          column: "os_version",
          identifier,
          reason: "OS version cannot exceed 50 characters.",
        });
        rowHasError = true;
      }

      if (!osHash) {
        errors.push({
          row: lineNum,
          column: "os_hash",
          identifier,
          reason: "OS hash is required for display_module.",
        });
        rowHasError = true;
      } else if (!OS_HASH_REGEX.test(osHash.trim())) {
        errors.push({
          row: lineNum,
          column: "os_hash",
          identifier,
          reason: "OS hash must be a 64-character hex SHA-256 string.",
        });
        rowHasError = true;
      }
    }

    if (!rowHasError) {
      validRows++;
    }
  });

  return {
    totalRows,
    validRows,
    invalidRows: totalRows - validRows,
    errors,
  };
}

/**
 * Downloads a structured CSV report of errors.
 */
export function downloadErrorsCsv(
  errors: Array<{ row: number; column?: string; identifier?: string; reason: string }>,
  filePrefix = "device_import_errors"
) {
  if (errors.length === 0) return;

  const escapeCsv = (str?: string) => `"${(str ?? "").replace(/"/g, '""')}"`;

  const header = ["Line", "Column", "Identifier", "Error Reason"].join(",");
  const rows = errors.map((e) =>
    [e.row, escapeCsv(e.column || "—"), escapeCsv(e.identifier || "—"), escapeCsv(e.reason)].join(",")
  );

  const csvContent = "\uFEFF" + [header, ...rows].join("\r\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);

  const a = document.createElement("a");
  a.href = url;
  a.download = `${filePrefix}_${timestamp}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
